import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Hash, MapPin, User, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { searchUsers, searchHashtags, searchPosts, getTrendingHashtags } from '../../lib/discoveryService';
import { User as UserType } from '../../types';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'users' | 'hashtags' | 'posts'>('all');
  const [users, setUsers] = useState<UserType[]>([]);
  const [hashtags, setHashtags] = useState<{ hashtag: string; count: number }[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [trendingHashtags, setTrendingHashtags] = useState<{ hashtag: string; count: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      loadTrendingHashtags();
      loadRecentSearches();
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim()) {
      performSearch();
    } else {
      clearResults();
    }
  }, [query, activeTab]);

  const loadTrendingHashtags = async () => {
    try {
      const trending = await getTrendingHashtags(10);
      setTrendingHashtags(trending);
    } catch (error) {
      console.error('Error loading trending hashtags:', error);
    }
  };

  const loadRecentSearches = () => {
    const recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setRecentSearches(recent.slice(0, 5));
  };

  const saveRecentSearch = (searchQuery: string) => {
    const recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const updated = [searchQuery, ...recent.filter((q: string) => q !== searchQuery)].slice(0, 10);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
    setRecentSearches(updated.slice(0, 5));
  };

  const clearRecentSearches = () => {
    localStorage.removeItem('recentSearches');
    setRecentSearches([]);
  };

  const performSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    try {
      if (activeTab === 'all' || activeTab === 'users') {
        const userResults = await searchUsers(query);
        setUsers(userResults);
      }
      
      if (activeTab === 'all' || activeTab === 'hashtags') {
        const hashtagResults = await searchHashtags(query);
        setHashtags(hashtagResults);
      }
      
      if (activeTab === 'all' || activeTab === 'posts') {
        const postResults = await searchPosts(query);
        setPosts(postResults);
      }

      saveRecentSearch(query);
    } catch (error) {
      console.error('Error performing search:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setUsers([]);
    setHashtags([]);
    setPosts([]);
  };

  const handleRecentSearchClick = (searchQuery: string) => {
    setQuery(searchQuery);
  };

  const handleTrendingHashtagClick = (hashtag: string) => {
    setQuery(`#${hashtag}`);
    setActiveTab('hashtags');
  };

  const handleUserClick = (username: string) => {
    onClose();
    // Navigation will be handled by the Link component
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center p-4 border-b border-gray-200">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users, hashtags, posts..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={onClose}
            className="ml-3 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        {query.trim() && (
          <div className="flex border-b border-gray-200">
            {['all', 'users', 'hashtags', 'posts'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`flex-1 py-3 px-4 text-sm font-medium capitalize ${
                  activeTab === tab
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          {!query.trim() ? (
            <div className="p-4 space-y-6">
              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Recent</h3>
                    <button
                      onClick={clearRecentSearches}
                      className="text-xs text-purple-600 hover:text-purple-700"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="space-y-2">
                    {recentSearches.map((search, index) => (
                      <button
                        key={index}
                        onClick={() => handleRecentSearchClick(search)}
                        className="flex items-center w-full p-2 text-left hover:bg-gray-50 rounded-md transition-colors"
                      >
                        <Search className="h-4 w-4 text-gray-400 mr-3" />
                        <span className="text-sm">{search}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Trending Hashtags */}
              {trendingHashtags.length > 0 && (
                <div>
                  <div className="flex items-center mb-3">
                    <TrendingUp className="h-4 w-4 text-gray-500 mr-2" />
                    <h3 className="text-sm font-medium text-gray-900">Trending</h3>
                  </div>
                  <div className="space-y-2">
                    {trendingHashtags.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleTrendingHashtagClick(item.hashtag)}
                        className="flex items-center justify-between w-full p-2 text-left hover:bg-gray-50 rounded-md transition-colors"
                      >
                        <div className="flex items-center">
                          <Hash className="h-4 w-4 text-gray-400 mr-3" />
                          <span className="text-sm font-medium">#{item.hashtag}</span>
                        </div>
                        <span className="text-xs text-gray-500">{item.count} posts</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Users Results */}
              {(activeTab === 'all' || activeTab === 'users') && users.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Users
                  </h3>
                  <div className="space-y-2">
                    {users.slice(0, activeTab === 'users' ? 20 : 3).map((user) => (
                      <Link
                        key={user.id}
                        to={`/profile/${user.username}`}
                        onClick={() => handleUserClick(user.username)}
                        className="flex items-center p-2 hover:bg-gray-50 rounded-md transition-colors"
                      >
                        <img
                          src={user.profileImage}
                          alt={user.username}
                          className="h-10 w-10 rounded-full object-cover mr-3"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{user.username}</p>
                          <p className="text-xs text-gray-500">{user.displayName}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Hashtags Results */}
              {(activeTab === 'all' || activeTab === 'hashtags') && hashtags.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <Hash className="h-4 w-4 mr-2" />
                    Hashtags
                  </h3>
                  <div className="space-y-2">
                    {hashtags.slice(0, activeTab === 'hashtags' ? 20 : 3).map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md transition-colors">
                        <div className="flex items-center">
                          <Hash className="h-4 w-4 text-gray-400 mr-3" />
                          <span className="text-sm font-medium">#{item.hashtag}</span>
                        </div>
                        <span className="text-xs text-gray-500">{item.count} posts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Posts Results */}
              {(activeTab === 'all' || activeTab === 'posts') && posts.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Posts</h3>
                  <div className="grid grid-cols-3 gap-1">
                    {posts.slice(0, activeTab === 'posts' ? 12 : 6).map((post) => {
                      const firstMedia = post.media && post.media.length > 0 ? post.media[0] : null;
                      const imageUrl = firstMedia?.media_url;

                      return (
                        <div key={post.id} className="relative pb-[100%] bg-gray-100 overflow-hidden">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt="Search result"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                              <span className="text-xs">No media</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No Results */}
              {users.length === 0 && hashtags.length === 0 && posts.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No results found</p>
                  <p className="text-sm">Try searching for something else</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};